import React, { useMemo } from 'react'

import { ApolloProvider } from '@apollo/client'
import { ContainsEditableProps, useEditor } from '@toasttab/sites-components'
import { v4 as uuidv4 } from 'uuid'

import { DoMenus_GetMenuGroupsGroup as DoMenusGetMenuGroupsGroup } from 'src/apollo/onlineOrdering'
import { BorderAndShadowConfig, MenuGroupIdentifier } from 'src/apollo/sites'

import { useBulkCalls } from 'shared/components/common/dynamic_menus/BulkCallsContext'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'

import GroupedMenuItems from './GroupedMenuItems'
import MenuGroupSkeleton from './MenuGroupSkeleton'
import { MenuGroupBlockBackupContent, MenuGroupBlockConfig } from './types'

type TestProps = { testId?: string }

export type MenuGroupEditableProps = {
  classNames?: string
  id: MenuGroupIdentifier
  backupContent: MenuGroupBlockBackupContent
  config: MenuGroupBlockConfig | null
  borderAndShadowConfig?: BorderAndShadowConfig | null
  overrideGlobalBorderAndShadowConfig?: boolean | null
  styles?: any
} & TestProps &
  ContainsEditableProps

const placeholderMenuGroup = {
  name: 'Menu Group',
  items: [
    { guid: uuidv4() },
    { guid: uuidv4() },
    { guid: uuidv4() },
    { guid: uuidv4() }
  ]
} as DoMenusGetMenuGroupsGroup

const EditableMenuGroup = (props: MenuGroupEditableProps) => {
  const { menuGroups: maybeMenuGroups, loading } = useBulkCalls()
  const { editPath, id, backupContent } = props

  const { useEditableRef } = useEditor()
  const { editableRef } = useEditableRef<HTMLDivElement>({
    name: 'menu group',
    displayName: 'Menu Group',
    actions: ['delete'],
    path: editPath,
    schema: { fields: [] }
  })

  const menuGroup = useMemo(() => {
    if (maybeMenuGroups.length > 0) {
      return (
        maybeMenuGroups.find((group) => group.guid === id.groupGuid) ||
        backupContent ||
        placeholderMenuGroup
      )
    } else {
      return backupContent || placeholderMenuGroup
    }
  }, [backupContent, id.groupGuid, maybeMenuGroups])

  if (loading) {
    return (
      <MenuGroupSkeleton
        columns={props.config?.appearance?.columns || 1}
        itemCount={backupContent?.items?.length || 4}
        showTitle={props.config?.appearance?.showMenuGroupTitle !== false}
      />
    )
  }

  return (
    <GroupedMenuItems
      {...props}
      itemsToRender={menuGroup.items}
      description={menuGroup.description || backupContent?.description}
      name={menuGroup.name || backupContent?.name || placeholderMenuGroup.name}
      editableRef={editableRef}
      groupGuid={id.groupGuid}
    />
  )
}

const ApolloWrappedEditableMenuGroup = (props: MenuGroupEditableProps) => {
  const ooClient = useOOClient()
  return (
    <ApolloProvider client={ooClient}>
      <EditableMenuGroup {...props} />
    </ApolloProvider>
  )
}

export default ApolloWrappedEditableMenuGroup
